import { rd, RemoteData } from '@passionware/monads';
import { makeFileURL } from 'lib/s3/s3Helper';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DropdownMenuItem } from 'v5/design-system/DropdownMenu';
import { WithServices } from 'v5/platform/ts/services';
import { WithToastService } from 'v5/services/front/ToastService/ToastService';
import {
  DEFAULT_FILES_PANEL_TILE_SIZE,
  type WithUserPreferencesService
} from 'v5/services/front/UserPreferencesService/UserPreferencesService';
import { WithUploadService } from 'v5/services/io/UploadService/UploadService';
import { BatchActionsLayout } from 'v5/patterns/entity-list/BatchActionsLayout';
import { EntityListHeader } from 'v5/patterns/entity-list/EntityListHeader';
import { TileSizeMenu } from 'v5/patterns/entity-list/TileSizeMenu';
import { TILE_MIN_WIDTH_PX } from 'v5/patterns/entity-list/tileSize';
import {
  MediaGallery,
  MediaGallerySkeleton
} from 'v5/patterns/forms/fields/image-upload/MediaGallery';
import { useLocalPreviews } from 'v5/patterns/forms/fields/image-upload/useLocalPreviews';
import {
  type PendingUpload,
  useMediaUploads
} from 'v5/patterns/forms/fields/image-upload/useMediaUploads';
import { PageInsetListLayout } from 'v5/patterns/page-layout/PageInsetListLayout';
import { MediaThumbnail } from 'v5/patterns/forms/fields/image-upload/MediaThumbnail';

export type FilesPanelDocument = {
  id: number;
  url?: string;
  archived?: boolean;
  order?: number | null;
  file?: {
    id?: string;
    name: string;
    mime_type?: string;
  };
};

export type FilesPanelCopy = {
  title: string;
  emptyTitle: string;
  emptyDescription: string;
  loadError: string;
  untitled: string;
  uploadedToast: string;
  uploadErrorToast: string;
  reorderErrorToast: string;
};

export interface FilesPanelProps
  extends WithServices<
    [WithUploadService, WithToastService, WithUserPreferencesService]
  > {
  documents: RemoteData<FilesPanelDocument[]>;
  /** Attaches a file already uploaded to storage. */
  onCreate: (payload: { file_id: string; url: string }) => Promise<unknown>;
  onDelete: (documentId: number) => Promise<unknown>;
  onReorder: (orderedIds: number[]) => Promise<unknown>;
  disabled: boolean;
  copy: FilesPanelCopy;
}

function fileHref(doc: FilesPanelDocument): string | null {
  if (doc.url && /^https?:\/\//i.test(doc.url)) {
    return doc.url;
  }
  const id = doc.file?.id;
  if (id) {
    return makeFileURL({
      ...doc.file,
      id: id as unknown as number,
      url: doc.url ?? ''
    });
  }
  return null;
}

/**
 * Files attached to an entity, as a gallery that takes new ones. On its host's background.
 * Unless disabled, tiles are picked (their circle, Shift, ⌘, a marquee from the gaps) and deleted
 * together from the batch menu or with Delete.
 */
export function FilesPanel({
  services,
  documents: documentsRd,
  onCreate,
  onDelete,
  onReorder,
  disabled,
  copy
}: FilesPanelProps) {
  const { uploadService, toastService, userPreferencesService } = services;
  const { t } = useTranslation('patterns', { keyPrefix: 'FilesPanel' });
  const uploads = useMediaUploads(uploadService, { secure: true });
  // from the Size menu: mine, the same on every resource and production
  const tileSizeStore = userPreferencesService.filesPanelTileSize;
  const tileSize = rd.mapOrElse(
    tileSizeStore.useValue(),
    value => value,
    DEFAULT_FILES_PANEL_TILE_SIZE
  );
  const tileMinWidth = `${TILE_MIN_WIDTH_PX[tileSize]}px`;
  /** Picked tiles, by key; only the ones listed count. */
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  /** Deleted from the batch menu, each waiting with a spinner. */
  const [removing, setRemoving] = useState<ReadonlySet<number>>(new Set());
  /** The order being saved, shown until the saved one comes back. */
  const [savingOrder, setSavingOrder] = useState<number[] | null>(null);
  const [thumbnailsShown, setThumbnailsShown] = useState(false);
  /** Uploaded files the host is attaching, still shown as uploads. */
  const [attaching, setAttaching] = useState<PendingUpload[]>([]);
  const previews = useLocalPreviews();
  const previewOf = (doc: FilesPanelDocument) =>
    doc.file?.id === undefined
      ? undefined
      : previews.previewOf(String(doc.file.id));

  // Files never reordered have no `order`: after the ordered ones, oldest first.
  const rank = (doc: FilesPanelDocument) => {
    const at = savingOrder ? savingOrder.indexOf(doc.id) : (doc.order ?? -1);
    return at === -1 ? Infinity : at;
  };
  const activeDocuments = rd.map(documentsRd, docs =>
    docs
      .filter(d => !d.archived)
      .sort((a, b) => rank(a) - rank(b) || a.id - b.id)
  );
  const count = rd.tryGet(activeDocuments)?.length ?? 0;
  const selectedIds = (rd.tryGet(activeDocuments) ?? [])
    .filter(doc => picked.has(String(doc.id)))
    .map(doc => doc.id);

  // A failed upload stays in the gallery with Retry; only a failed attach
  // toasts. An uploaded file stays an upload until the host lists it, which
  // it does before `onCreate` resolves.
  const add = async (file: File) => {
    const outcome = await uploads.upload(file);
    if (outcome.type === 'failed') return;
    const { fileId, accessUrl, previewUrl = null } = outcome.media;
    const id = `attach-${fileId}`;
    setAttaching(current => [
      ...current,
      { id, file, previewUrl, status: 'uploading', progress: 100 }
    ]);
    if (previewUrl) previews.keep(fileId, previewUrl);
    try {
      await onCreate({ file_id: fileId, url: accessUrl });
      toastService.open({ type: 'success', message: copy.uploadedToast });
    } catch {
      previews.release(fileId);
      toastService.open({ type: 'error', message: copy.uploadErrorToast });
    } finally {
      setAttaching(current => current.filter(upload => upload.id !== id));
    }
  };

  /** One file from its tile or Delete, or the selection from the batch menu; one toast a call. */
  const remove = async (ids: number[]) => {
    setRemoving(current => new Set([...current, ...ids]));
    setPicked(current => {
      const next = new Set(current);
      ids.forEach(id => next.delete(String(id)));
      return next;
    });
    const results = await Promise.allSettled(
      ids.map(async id => {
        const fileId = rd.tryGet(documentsRd)?.find(doc => doc.id === id)
          ?.file?.id;
        await onDelete(id);
        if (fileId !== undefined) previews.release(String(fileId));
      })
    );
    setRemoving(current => {
      const next = new Set(current);
      ids.forEach(id => next.delete(id));
      return next;
    });
    const failed = results.filter(result => result.status === 'rejected');
    toastService.open(
      failed.length === 0
        ? {
            type: 'success',
            message: t('toast.deleted', { count: ids.length })
          }
        : {
            type: 'error',
            message: t('toast.deleteFailed', { count: failed.length })
          }
    );
  };

  const reorder = async (keys: string[]) => {
    const ids = keys.map(Number);
    setSavingOrder(ids);
    try {
      await onReorder(ids);
    } catch {
      toastService.open({ type: 'error', message: copy.reorderErrorToast });
    } finally {
      setSavingOrder(null);
    }
  };

  return (
    <div className="tailwind-root contents">
      <PageInsetListLayout
        className="bg-transparent"
        header={<EntityListHeader title={copy.title} count={rd.of(count)} />}
        actions={
          <>
            {count > 0 && (
              <TileSizeMenu
                value={tileSize}
                onChange={value => void tileSizeStore.setValue(value)}
              />
            )}
            {!disabled && (
              <BatchActionsLayout
                selectedCount={selectedIds.length}
                entityName={{ singular: t('file'), plural: t('files') }}
                onClearSelection={() => setPicked(new Set())}
              >
                <DropdownMenuItem
                  className="text-fg-danger focus:text-fg-danger"
                  onSelect={() => void remove(selectedIds)}
                >
                  <Trash2 />
                  {t('deleteSelected', { count: selectedIds.length })}
                </DropdownMenuItem>
              </BatchActionsLayout>
            )}
          </>
        }
        // This scroll area clips at its top edge, where the gallery's tiles
        // start: room for their focus ring.
        contentClassName="pt-[4px]"
        content={rd
          .journey(activeDocuments)
          .wait(<MediaGallerySkeleton tileMinWidth={tileMinWidth} />)
          .catch(() => (
            <p className="text-sm text-fg-danger">{copy.loadError}</p>
          ))
          .map(docs => (
            <MediaGallery
              services={services}
              items={docs.map(doc => ({
                key: String(doc.id),
                name: doc.file?.name ?? copy.untitled,
                mimeType: doc.file?.mime_type,
                src: previewOf(doc) ?? fileHref(doc) ?? '',
                removing: removing.has(doc.id)
              }))}
              uploads={[...uploads.uploads, ...attaching]}
              onAddFiles={
                disabled ? undefined : files => files.forEach(f => void add(f))
              }
              onRemove={disabled ? undefined : key => remove([Number(key)])}
              onReorder={disabled ? undefined : keys => void reorder(keys)}
              selectedKeys={disabled ? undefined : picked}
              onSelectedKeysChange={setPicked}
              onRetryUpload={upload => {
                uploads.dismiss(upload.id);
                void add(upload.file);
              }}
              onDismissUpload={uploads.dismiss}
              accept="image/*,video/*,application/pdf"
              maxSize={10 * 1024 * 1024}
              multiple
              tileMinWidth={tileMinWidth}
              renderMedia={item => <MediaThumbnail item={item} />}
              addTile={<MediaGallery.AddTile />}
              thumbnailsShown={thumbnailsShown}
              onThumbnailsShownChange={setThumbnailsShown}
              emptyText={
                <div className="text-center">
                  <p className="text-base font-medium text-fg-default">
                    {copy.emptyTitle}
                  </p>
                  <p className="mt-2 text-sm text-fg-muted">
                    {copy.emptyDescription}
                  </p>
                </div>
              }
            />
          ))}
      />
    </div>
  );
}
